import { Controller } from "@hotwired/stimulus";
import { marked } from "marked";

export default class extends Controller {
  static values = {
    url: String,
    autoScroll: { type: Boolean, default: true },
    inputArea: String,
  };

  connect() {
    if (!this.urlValue) {
      console.warn("[chat-stream] Missing urlValue");
      return;
    }

    marked.setOptions({
      breaks: true,
      gfm: true,
    });

    this.buffer = "";
    this.displayedLength = 0;
    this.isAnimating = false;
    this.isDone = false;
    this.disableInputArea(true);
    this.eventSource = new EventSource(this.urlValue);
    this.eventSource.addEventListener("delta", this.onDelta);
    this.eventSource.addEventListener("done", this.onDone);
    this.eventSource.addEventListener("error", this.onError);
  }

  disconnect() {
    this.close();
  }

  close() {
    if (!this.eventSource) return;
    this.eventSource.removeEventListener("delta", this.onDelta);
    this.eventSource.removeEventListener("done", this.onDone);
    this.eventSource.removeEventListener("error", this.onError);
    this.eventSource.close();
    this.eventSource = null;
  }

  onDelta = (event) => {
    const payload = JSON.parse(event.data);
    const delta = payload?.delta ?? "";
    if (!delta) return;

    this.buffer += delta;

    if (!this.isAnimating) {
      this.animateText();
    }
  };

  animateText() {
    if (this.isAnimating) return;

    this.isAnimating = true;
    const animate = () => {
      if (this.displayedLength < this.buffer.length) {
        const charsToAdd = Math.floor(Math.random() * 2 + 1);
        this.displayedLength = Math.min(
          this.displayedLength + charsToAdd,
          this.buffer.length,
        );

        const displayedMarkdown = this.buffer.substring(
          0,
          this.displayedLength,
        );
        this.element.innerHTML = marked.parse(displayedMarkdown);

        if (this.autoScrollValue) this.scrollToBottom();

        requestAnimationFrame(animate);
      } else {
        this.isAnimating = false;

        if (this.displayedLength < this.buffer.length) {
          this.animateText();
        }
      }
    };

    requestAnimationFrame(animate);
  }

  onDone = (event) => {
    const payload = JSON.parse(event.data);

    this.isDone = true;
    this.close();

    // Wait for animation to complete before finalizing
    this.waitForAnimationComplete().then(() => {
      this.disableInputArea(["pending", "closed"].includes(payload?.status));
      this.dispatch("done", { ...payload, text: this.buffer });
    });
  };

  waitForAnimationComplete() {
    return new Promise((resolve) => {
      const checkAnimation = () => {
        if (!this.isAnimating && this.displayedLength >= this.buffer.length) {
          resolve();
        } else {
          requestAnimationFrame(checkAnimation);
        }
      };
      checkAnimation();
    });
  }

  onError = (event) => {
    console.error("[chat-stream] SSE error", event);
    this.close();
    this.dispatch("error", {
      detail: { message: "Stream error or disconnected." },
    });
  };

  scrollToBottom() {
    const scroller = this.element.closest("[data-scroll-container]");
    if (!scroller) {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      });
      return;
    }
    requestAnimationFrame(() => {
      scroller.scrollTop = scroller.scrollHeight;
    });
  }

  disableInputArea(disabled = true) {
    const fieldset = document.querySelector(`${this.inputAreaValue} fieldset`);

    if (fieldset) {
      fieldset.disabled = disabled;
      if (!disabled && window.matchMedia("(pointer: fine)").matches)
        fieldset.querySelector("textarea").focus();
    }
  }
}
